/**
 * apps/battle/client/src/terminal/state/unlockDiff.ts
 * Compute newly-unlocked clusters and newly-available portals between two /api/map polls.
 *
 * Used by the cluster exit narrative screen to render the
 *   "Cluster B is now unlocked. A new portal shimmers between …"
 * lines. Capped at 3 entries each per spec §15 risk row to keep the screen tidy.
 */
import type { MapResp } from '../../api.js';

export interface UnlockDiff {
  newlyUnlockedClusters: { clusterId: string; name: string }[];
  newlyAvailablePortals: { portalKey: string; tier: number }[];
}

export function diffMap(prev: MapResp | null, next: MapResp | null): UnlockDiff {
  if (!prev || !next) return { newlyUnlockedClusters: [], newlyAvailablePortals: [] };

  const prevLocked = new Map(prev.clusters.map((c) => [c.clusterId, c.locked]));
  const prevPortalAvail = new Map(prev.portals.map((p) => [p.portalKey, p.available]));

  const newlyUnlockedClusters = next.clusters
    .filter((c) => !c.locked && prevLocked.get(c.clusterId) === true)
    .map((c) => ({ clusterId: c.clusterId, name: c.fullName }));

  const newlyAvailablePortals = next.portals
    .filter((p) => p.available && prevPortalAvail.get(p.portalKey) === false)
    .map((p) => ({ portalKey: p.portalKey, tier: p.tier }));

  return { newlyUnlockedClusters, newlyAvailablePortals };
}

/** True iff cluster X transitioned from un-cleared → cleared between two polls. */
export function clusterJustCleared(
  prev: MapResp | null,
  next: MapResp | null,
  clusterId: string,
): boolean {
  if (!prev || !next) return false;
  const before = prev.clusters.find((c) => c.clusterId === clusterId);
  const after  = next.clusters.find((c) => c.clusterId === clusterId);
  if (!before || !after) return false;
  const wasCleared = before.clearedLatched || before.stageCleared;
  const nowCleared = after.clearedLatched  || after.stageCleared;
  return !wasCleared && nowCleared;
}
